import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Pencil, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  CreateCourseDialog,
  type NewCourseFields,
} from '../../components/editor/create-course-dialog'
import { Button, buttonVariants } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Chip } from '../../components/ui/chip'
import { Field } from '../../components/ui/field'
import { FilterBar, FilterSelect } from '../../components/ui/filter-bar'
import { SearchInput } from '../../components/ui/search-input'
import { Select } from '../../components/ui/select'
import { errorCode, errorReason, sessionGone } from '../../lib/errors'
import { EmptyState, ErrorState, Loading, PageHeading, Problem } from '../components/chrome'
import { CourseStatus } from '../components/company-parts'
import { usePasskeyNote } from '../components/passkey-note'
import { useReauth } from '../components/reauth'
import { useI18n } from '../lib/i18n'
import {
  ArchiveAction,
  DuplicateAction,
  DuplicationStatus,
  useDuplication,
  type CardNotice,
} from './catalog-actions'
import {
  companyGone,
  organizationChanged,
  useCatalog,
  useCompanies,
  type CatalogCourse,
  type CompaniesQuery,
} from '../lib/screens'
import { foldForSearch, matchesSearch, SEARCH_MAX } from '../lib/search'
import { forCompany, useTRPCClient } from '../lib/trpc'

/*
 * «Cursos» (PRD 04 §2.4, ADR 0008 §3; Figma 86:2693): Eklevo's catalogue, as cards with the
 * course's level in its series, title, state, lessons, duration and in how many companies it is
 * «Activo» (HR launched it). No lesson, question nor answer. A published course is activated
 * for one company at a time («Activar en una empresa»): it stays «No lanzado» until the
 * company's HR launches it, as `program activate` does. The search (by title) and the level
 * filter happen in the page. «Crear nuevo curso» (PRD 04 §2.5, the shared dialog without
 * «Número de niveles», ADR 0009 §4) creates a draft and opens the editor; «Editar» opens a
 * course that is not archived, a draft never published too (ADR 0009 §1). «Duplicar», on every
 * course, and «Archivar», on a published one (ADR 0009 §8), are in catalog-actions.tsx; a
 * duplicate runs in the background, and its card says where it stands. Without
 * the Figma's description. Each view leaves `backoffice.viewed` in Eklevo's audit trail. Marked
 * for design review.
 */
export function CatalogPage() {
  const { t, locale } = useI18n()
  const catalog = useCatalog()
  const client = useTRPCClient()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const createButton = useRef<HTMLButtonElement>(null)
  // Through the query client's cache, whose errors end a session the server no longer knows.
  const { mutateAsync: createCourse } = useMutation({
    mutationFn: (fields: NewCourseFields) => client.catalog.create.mutate(fields),
    gcTime: 0,
  })
  const create = (
    <Button ref={createButton} onClick={() => setCreating(true)}>
      <Plus aria-hidden className="size-4" />
      {t('catalog.create')}
    </Button>
  )
  const [text, setText] = useState('')
  const [level, setLevel] = useState('')
  // The companies to activate a course for, asked for once the first card needs them.
  const [choosing, setChoosing] = useState(false)
  const companies = useCompanies(choosing)
  // The duplicate each card started, here so that a card the filters hide goes on following its own.
  const [duplications, setDuplications] = useState<Readonly<Record<string, string>>>({})

  const wanted = foldForSearch(text, locale)
  const filtered = Boolean(wanted || level)
  const all = catalog.data?.courses ?? []
  const levels = [...new Set(all.flatMap((c) => (c.level === null ? [] : [c.level])))].sort(
    (a, b) => a - b,
  )
  const courses = all.filter(
    (c) => (!level || String(c.level) === level) && matchesSearch(c.title ?? '', wanted, locale),
  )
  const clear = () => {
    setText('')
    setLevel('')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading title={t('catalog.title')}>{t('catalog.body')}</PageHeading>
        {create}
      </div>
      <CreateCourseDialog
        locale={locale}
        open={creating}
        onOpenChange={setCreating}
        opener={createButton}
        create={createCourse}
        onCreated={(courseId) =>
          void navigate({ to: '/cursos/$courseId/editar', params: { courseId } })
        }
      />
      {catalog.isPending ? (
        <Loading />
      ) : catalog.isError ? (
        <ErrorState onRetry={() => void catalog.refetch()} />
      ) : all.length === 0 ? (
        <EmptyState title={t('catalog.empty.title')}>{t('catalog.empty.body')}</EmptyState>
      ) : (
        <>
          <FilterBar label={t('catalog.filters.label')}>
            <SearchInput
              value={text}
              onChange={setText}
              label={t('catalog.search')}
              placeholder={t('catalog.search')}
              maxLength={SEARCH_MAX}
            />
            {levels.length > 0 && (
              <FilterSelect
                label={t('catalog.filter.level')}
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="">{t('catalog.filter.allLevels')}</option>
                {levels.map((l) => (
                  <option key={l} value={String(l)}>
                    {t('catalog.level', { level: l })}
                  </option>
                ))}
              </FilterSelect>
            )}
            {filtered && (
              <Button variant="ghost" size="sm" onClick={clear}>
                {t('common.filters.clear')}
              </Button>
            )}
          </FilterBar>
          {courses.length === 0 ? (
            <EmptyState title={t('catalog.noResults')}>
              <Button variant="secondary" size="sm" onClick={clear}>
                {t('common.filters.clear')}
              </Button>
            </EmptyState>
          ) : (
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {courses.map((c) => (
                <li key={c.id}>
                  <CourseCard
                    course={c}
                    companies={companies}
                    onChoose={() => setChoosing(true)}
                    duplicationJob={duplications[c.id] ?? null}
                    onDuplicationStarted={(jobId) =>
                      setDuplications((started) => ({ ...started, [c.id]: jobId }))
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-ink-muted">{t('catalog.activeInHint')}</p>
        </>
      )}
    </div>
  )
}

function CourseCard({
  course,
  companies,
  onChoose,
  duplicationJob,
  onDuplicationStarted,
}: {
  course: CatalogCourse
  companies: CompaniesQuery
  onChoose: () => void
  duplicationJob: string | null
  onDuplicationStarted: (jobId: string) => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const opener = useRef<HTMLButtonElement>(null)
  const close = () => {
    setOpen(false)
    // The panel's buttons are gone: the focus goes back to what opened it.
    requestAnimationFrame(() => opener.current?.focus())
  }
  const title = titleOf(course, t)
  // What «Archivar» says once done (its button is gone with it, so the focus goes there), or why
  // it could not be. The focus moves once the line is on the page: not in the next frame, which
  // may come before React has rendered it.
  const [notice, setNotice] = useState<CardNotice | null>(null)
  const done = useRef<HTMLParagraphElement>(null)
  const duplication = useDuplication(duplicationJob)
  useEffect(() => {
    if (notice?.tone === 'done') done.current?.focus()
  }, [notice])
  return (
    <Card className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2">
        {course.level !== null && <Chip>{t('catalog.level', { level: course.level })}</Chip>}
        <CourseStatus status={course.status} />
      </div>
      <h2 className="mt-3 text-lg font-medium [overflow-wrap:anywhere]">{titleOf(course, t)}</h2>
      <p className="mt-2 text-sm text-ink-muted">
        {t('catalog.lessons', { count: course.lessons })}
        {course.durationMinutes !== null && (
          <>
            {' · '}
            {t('catalog.duration', { minutes: course.durationMinutes })}
          </>
        )}
      </p>
      <p className="mt-1 text-sm">{t('catalog.activeIn', { count: course.activeCompanies })}</p>
      <div className="mt-auto flex flex-wrap gap-2 pt-5">
        {course.status !== 'archived' && (
          <Link
            to="/cursos/$courseId/editar"
            params={{ courseId: course.id }}
            aria-label={t('catalog.edit.label', { course: title })}
            className={buttonVariants({ variant: 'secondary', size: 'sm' })}
          >
            <Pencil aria-hidden className="size-4" />
            {t('catalog.edit')}
          </Link>
        )}
        {course.status === 'published' && !open && (
          <Button
            ref={opener}
            variant="secondary"
            size="sm"
            aria-label={t('catalog.activate.label', { course: title })}
            onClick={() => {
              onChoose()
              setOpen(true)
            }}
          >
            {t('catalog.activate')}
          </Button>
        )}
        <DuplicateAction
          course={course}
          title={title}
          running={duplication.status === 'running'}
          onStarted={onDuplicationStarted}
        />
        {course.status === 'published' && (
          <ArchiveAction course={course} title={title} onNotice={setNotice} />
        )}
      </div>
      {course.status === 'published' && open && (
        <div className="mt-3">
          <ActivatePanel course={course} companies={companies} onClose={close} />
        </div>
      )}
      {notice?.tone === 'done' && (
        // Read out as the focus lands on it: the button that asked for it is gone.
        <p ref={done} tabIndex={-1} className="mt-3 text-sm text-success outline-none">
          {notice.text}
        </p>
      )}
      <Problem>{notice?.tone === 'error' ? notice.text : ''}</Problem>
      <DuplicationStatus duplication={duplication} />
    </Card>
  )
}

/**
 * A course's title, or the page's own text for a course of the editor never published whose
 * draft has none the catalogue can show (ADR 0009 §2: a draft may hold anything).
 */
function titleOf(course: CatalogCourse, t: ReturnType<typeof useI18n>['t']): string {
  return course.title ?? t('catalog.untitled')
}

/** What the panel says once the activation is done, or why it could not be. */
interface Outcome {
  tone: 'done' | 'error'
  text: string
}

/**
 * «Activar en una empresa»: a company to choose; it is opened first (`companies.open`, the
 * session's working organization, with its entry in that company's trail), and a company that
 * already has the course is told so without asking for the passkey (ADR 0008, N-a.2). Otherwise
 * the passkey again if it was not touched in the last minutes, in a dialog that names the course
 * and the company, then `companies.activateCourse`, with the company as the one the page expects.
 * The panel stays open, to activate the course for another company.
 */
function ActivatePanel({
  course,
  companies,
  onClose,
}: {
  course: CatalogCourse
  companies: CompaniesQuery
  onClose: () => void
}) {
  const { t } = useI18n()
  const client = useTRPCClient()
  const reauth = useReauth()
  // In the field's hint, so that the company's list is described with it.
  const passkeyNote = usePasskeyNote()
  const [organizationId, setOrganizationId] = useState('')
  const [missing, setMissing] = useState(false)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const select = useRef<HTMLSelectElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const submitButton = useRef<HTMLButtonElement>(null)

  // The button that opened the panel is gone: the focus goes to the company to choose once the
  // list is there, and to «Cerrar» until then or without any.
  useEffect(() => {
    ;(select.current ?? closeButton.current)?.focus()
  }, [companies.isSuccess])

  const nameOf = (id: string) => companies.data?.companies.find((c) => c.id === id)?.name ?? ''

  const activate = useMutation({
    mutationFn: async (orgId: string) => {
      const from = submitButton.current
      const detail = await client.companies.open.mutate({ organizationId: orgId })
      if (detail.courses.some((c) => c.courseId === course.id)) return 'already' as const
      const done = await reauth.run(
        () => client.companies.activateCourse.mutate({ courseId: course.id }, forCompany(orgId)),
        {
          from,
          purpose: t('catalog.activate.purpose', {
            course: titleOf(course, t),
            company: detail.name,
          }),
        },
      )
      return done ? ('done' as const) : null
    },
    onSuccess: (r, orgId) => {
      if (r === 'done')
        setOutcome({ tone: 'done', text: t('catalog.activate.done', { company: nameOf(orgId) }) })
      else if (r === 'already')
        setOutcome({
          tone: 'error',
          text: t('catalog.activate.already', { company: nameOf(orgId) }),
        })
    },
    onError: (err, orgId) => {
      if (sessionGone(err)) return
      const reason = errorReason(err)
      const text =
        reason === 'already-activated'
          ? t('catalog.activate.already', { company: nameOf(orgId) })
          : companyGone(err)
            ? t('catalog.activate.unavailable')
            : organizationChanged(err)
              ? t('common.error.organizationChanged')
              : errorCode(err) === 'TOO_MANY_REQUESTS'
                ? t('common.error.rateLimited')
                : t('common.error.generic')
      setOutcome({ tone: 'error', text })
    },
  })

  const submit = () => {
    if (activate.isPending) return
    setOutcome(null)
    if (!organizationId) {
      setMissing(true)
      select.current?.focus()
      return
    }
    activate.mutate(organizationId)
  }

  const list = companies.data?.companies ?? []
  return (
    <div className="rounded-lg border border-line p-4">
      <h3 className="font-medium">{t('catalog.activate')}</h3>
      {companies.isPending ? (
        <p role="status" className="mt-2 text-sm text-ink-muted">
          {t('common.loading')}
        </p>
      ) : companies.isError ? (
        <>
          <Problem>{t('common.error.generic')}</Problem>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => void companies.refetch()}
          >
            {t('common.retry')}
          </Button>
        </>
      ) : list.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">{t('catalog.activate.noCompanies')}</p>
      ) : (
        <>
          <Field
            label={t('catalog.activate.company')}
            hint={
              passkeyNote
                ? `${t('catalog.activate.hint')} ${passkeyNote}`
                : t('catalog.activate.hint')
            }
            error={missing ? t('catalog.activate.required') : null}
            className="mt-3"
          >
            <Select
              ref={select}
              value={organizationId}
              onChange={(e) => {
                setOrganizationId(e.target.value)
                setMissing(false)
                setOutcome(null)
              }}
            >
              <option value="">{t('catalog.activate.choose')}</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.status === 'suspended'
                    ? t('catalog.activate.suspended', { company: c.name })
                    : c.name}
                </option>
              ))}
            </Select>
          </Field>
          <p role="status" className="text-sm text-success not-empty:mt-3">
            {outcome?.tone === 'done' ? outcome.text : ''}
          </p>
          <Problem>{outcome?.tone === 'error' ? outcome.text : ''}</Problem>
        </>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        {list.length > 0 && (
          <Button
            ref={submitButton}
            size="sm"
            aria-disabled={activate.isPending || undefined}
            className="aria-disabled:cursor-progress aria-disabled:opacity-50"
            onClick={submit}
          >
            {activate.isPending ? t('catalog.activate.busy') : t('catalog.activate.submit')}
          </Button>
        )}
        <Button ref={closeButton} variant="secondary" size="sm" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  )
}
